import { useMemo } from 'react';
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
  LabelList,
} from 'recharts';

interface Player {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  resident_club: string;
  handicap: number;
  category: string;
  status: string;
  created_at: string;
  country: string;
  tournament_id: string;
  registration_status: {
    personalInfo: boolean;
    payment: boolean;
    verification: boolean;
  };
  payment_proof_url?: string;
  shirt_size?: string;
  registration_number?: string;
  gender?: string;
}

interface PlayerAnalyticsTabProps {
  players: Player[];
}

const COLORS = ['#2563eb', '#16a34a', '#d97706', '#dc2626', '#0891b2', '#7c3aed', '#be185d', '#65a30d', '#ea580c', '#0284c7'];

function AnalyticsCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <h3 className="text-base font-semibold text-gray-800 mb-4">{title}</h3>
      {children}
    </div>
  );
}

function StatRow({ label, value, total, color }: { label: string; value: number; total: number; color: string }) {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <div className="mb-3">
      <div className="flex justify-between items-center mb-1">
        <span className="text-sm text-gray-600 truncate max-w-[60%]">{label}</span>
        <span className="text-sm font-medium text-gray-800">{value} <span className="text-gray-400 font-normal">({pct}%)</span></span>
      </div>
      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{ width: `${pct}%`, backgroundColor: color }}
        />
      </div>
    </div>
  );
}

export default function PlayerAnalyticsTab({ players }: PlayerAnalyticsTabProps) {
  const total = players.length;

  const genderBreakdown = useMemo(() => {
    const counts: Record<string, number> = {};
    players.forEach(p => {
      const g = p.gender ? p.gender.charAt(0).toUpperCase() + p.gender.slice(1).toLowerCase() : 'Not Specified';
      counts[g] = (counts[g] || 0) + 1;
    });
    return Object.entries(counts).map(([name, value]) => ({ name, value }));
  }, [players]);

  const categoryBreakdown = useMemo(() => {
    const counts: Record<string, number> = {};
    players.forEach(p => {
      const c = p.category || 'Unknown';
      counts[c] = (counts[c] || 0) + 1;
    });
    return Object.entries(counts).map(([name, value]) => ({ name, value }));
  }, [players]);

  const countryBreakdown = useMemo(() => {
    const counts: Record<string, number> = {};
    const labels: Record<string, string> = {};
    players.forEach(p => {
      const raw = (p.country || 'Unknown').trim().replace(/\s+/g, ' ');
      const key = raw.toLowerCase();
      counts[key] = (counts[key] || 0) + 1;
      if (!labels[key]) labels[key] = raw;
    });
    return Object.entries(counts)
      .map(([key, value]) => ({ name: labels[key], value }))
      .sort((a, b) => b.value - a.value);
  }, [players]);

  const clubBreakdown = useMemo(() => {
    const counts: Record<string, number> = {};
    const labels: Record<string, string> = {};
    players.forEach(p => {
      const raw = (p.resident_club || 'Unknown').trim().replace(/\s+/g, ' ');
      const key = raw.toLowerCase();
      counts[key] = (counts[key] || 0) + 1;
      if (!labels[key]) labels[key] = raw;
    });
    return Object.entries(counts)
      .map(([key, value]) => ({ name: labels[key], value }))
      .sort((a, b) => b.value - a.value);
  }, [players]);

  const handicapByGender = useMemo(() => {
    const maleBrackets = [
      { label: 'Male 0–18', min: 0, max: 18, gender: 'male' },
      { label: 'Male 19–28', min: 19, max: 28, gender: 'male' },
    ];
    const femaleBrackets = [
      { label: 'Ladies 0–18', min: 0, max: 18, gender: 'female' },
      { label: 'Ladies 19–36', min: 19, max: 36, gender: 'female' },
    ];

    const isMale = (p: Player) => {
      const g = (p.gender || '').toLowerCase();
      return g === 'male' || g === 'm';
    };
    const isFemale = (p: Player) => {
      const g = (p.gender || '').toLowerCase();
      return g === 'female' || g === 'f' || g === 'lady' || g === 'ladies';
    };

    const males = players.filter(isMale);
    const females = players.filter(isFemale);

    const maleData = maleBrackets.map(b => ({
      name: b.label,
      count: males.filter(p => p.handicap >= b.min && p.handicap <= b.max).length,
      gender: 'Male',
    }));

    const femaleData = femaleBrackets.map(b => ({
      name: b.label,
      count: females.filter(p => p.handicap >= b.min && p.handicap <= b.max).length,
      gender: 'Ladies',
    }));

    return [...maleData, ...femaleData];
  }, [players]);

  const handicapSummary = useMemo(() => {
    const isMale = (p: Player) => { const g = (p.gender || '').toLowerCase(); return g === 'male' || g === 'm'; };
    const isFemale = (p: Player) => { const g = (p.gender || '').toLowerCase(); return g === 'female' || g === 'f' || g === 'lady' || g === 'ladies'; };
    const males = players.filter(isMale);
    const females = players.filter(isFemale);
    return [
      { label: 'Male 0–18', value: males.filter(p => p.handicap >= 0 && p.handicap <= 18).length, color: '#2563eb' },
      { label: 'Male 19–28', value: males.filter(p => p.handicap >= 19 && p.handicap <= 28).length, color: '#60a5fa' },
      { label: 'Ladies 0–18', value: females.filter(p => p.handicap >= 0 && p.handicap <= 18).length, color: '#16a34a' },
      { label: 'Ladies 19–36', value: females.filter(p => p.handicap >= 19 && p.handicap <= 36).length, color: '#4ade80' },
    ];
  }, [players]);

  const shirtSizeBreakdown = useMemo(() => {
    const order = ['XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL'];
    const isMale = (p: Player) => { const g = (p.gender || '').toLowerCase(); return g === 'male' || g === 'm'; };
    const isFemale = (p: Player) => { const g = (p.gender || '').toLowerCase(); return g === 'female' || g === 'f' || g === 'lady' || g === 'ladies'; };
    const maleCounts: Record<string, number> = {};
    const femaleCounts: Record<string, number> = {};
    players.forEach(p => {
      const s = p.shirt_size ? p.shirt_size.toUpperCase() : null;
      if (!s) return;
      if (isMale(p)) maleCounts[s] = (maleCounts[s] || 0) + 1;
      else if (isFemale(p)) femaleCounts[s] = (femaleCounts[s] || 0) + 1;
      else {
        maleCounts[s] = (maleCounts[s] || 0) + 1;
      }
    });
    const allSizes = new Set([...Object.keys(maleCounts), ...Object.keys(femaleCounts)]);
    const sorted = order.filter(s => allSizes.has(s)).map(s => ({
      name: s,
      Male: maleCounts[s] || 0,
      Female: femaleCounts[s] || 0,
    }));
    return sorted;
  }, [players]);

  const paymentBreakdown = useMemo(() => {
    const paid = players.filter(p => p.registration_status?.payment === true).length;
    const pending = total - paid;
    return [
      { name: 'Paid', value: paid },
      { name: 'Pending', value: pending },
    ];
  }, [players, total]);

  const statusBreakdown = useMemo(() => {
    const counts: Record<string, number> = {};
    players.forEach(p => {
      const s = p.status ? p.status.charAt(0).toUpperCase() + p.status.slice(1).toLowerCase() : 'Unknown';
      counts[s] = (counts[s] || 0) + 1;
    });
    return Object.entries(counts).map(([name, value]) => ({ name, value }));
  }, [players]);

  const avgHandicap = useMemo(() => {
    if (total === 0) return 0;
    return (players.reduce((sum, p) => sum + (p.handicap || 0), 0) / total).toFixed(1);
  }, [players, total]);

  const lowestHcp = useMemo(() => {
    if (total === 0) return '—';
    return Math.min(...players.map(p => p.handicap || 0));
  }, [players, total]);

  const highestHcp = useMemo(() => {
    if (total === 0) return '—';
    return Math.max(...players.map(p => p.handicap || 0));
  }, [players, total]);

  if (total === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-gray-400">
        <p className="text-lg font-medium">No player data available</p>
        <p className="text-sm mt-1">Register players to see analytics</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { label: 'Total Players', value: total, sub: `${players.filter(p => p.registration_status?.payment).length} paid` },
          { label: 'Avg Handicap', value: avgHandicap, sub: `Low ${lowestHcp} / High ${highestHcp}` },
          { label: 'Countries', value: new Set(players.map(p => p.country).filter(Boolean)).size, sub: 'represented' },
          { label: 'Clubs', value: new Set(players.map(p => p.resident_club).filter(Boolean)).size, sub: 'resident clubs' },
        ].map(kpi => (
          <div key={kpi.label} className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
            <p className="text-sm text-gray-500">{kpi.label}</p>
            <p className="text-2xl font-bold text-gray-800 mt-1">{kpi.value}</p>
            <p className="text-xs text-gray-400 mt-1">{kpi.sub}</p>
          </div>
        ))}
      </div>

      {/* Row 1: Gender + Category + Payment */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <AnalyticsCard title="Gender Breakdown">
          <ResponsiveContainer width="100%" height={180}>
            <PieChart>
              <Pie data={genderBreakdown} cx="50%" cy="50%" outerRadius={65} dataKey="value" label={({ name, percent }) => `${name} ${Math.round(percent * 100)}%`} labelLine={false} fontSize={11}>
                {genderBreakdown.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip formatter={(v: number) => [v, 'Players']} />
            </PieChart>
          </ResponsiveContainer>
          <div className="mt-2 space-y-1">
            {genderBreakdown.map((item, i) => (
              <StatRow key={item.name} label={item.name} value={item.value} total={total} color={COLORS[i % COLORS.length]} />
            ))}
          </div>
        </AnalyticsCard>

        <AnalyticsCard title="Category Breakdown">
          <ResponsiveContainer width="100%" height={180}>
            <PieChart>
              <Pie data={categoryBreakdown} cx="50%" cy="50%" outerRadius={65} dataKey="value" label={({ name, percent }) => `${name} ${Math.round(percent * 100)}%`} labelLine={false} fontSize={11}>
                {categoryBreakdown.map((_, i) => <Cell key={i} fill={COLORS[(i + 2) % COLORS.length]} />)}
              </Pie>
              <Tooltip formatter={(v: number) => [v, 'Players']} />
            </PieChart>
          </ResponsiveContainer>
          <div className="mt-2 space-y-1">
            {categoryBreakdown.map((item, i) => (
              <StatRow key={item.name} label={item.name} value={item.value} total={total} color={COLORS[(i + 2) % COLORS.length]} />
            ))}
          </div>
        </AnalyticsCard>

        <AnalyticsCard title="Payment Status">
          <ResponsiveContainer width="100%" height={180}>
            <PieChart>
              <Pie data={paymentBreakdown} cx="50%" cy="50%" outerRadius={65} dataKey="value" label={({ name, percent }) => `${name} ${Math.round(percent * 100)}%`} labelLine={false} fontSize={11}>
                <Cell fill="#16a34a" />
                <Cell fill="#d97706" />
              </Pie>
              <Tooltip formatter={(v: number) => [v, 'Players']} />
            </PieChart>
          </ResponsiveContainer>
          <div className="mt-2 space-y-1">
            <StatRow label="Paid" value={paymentBreakdown[0]?.value || 0} total={total} color="#16a34a" />
            <StatRow label="Pending" value={paymentBreakdown[1]?.value || 0} total={total} color="#d97706" />
          </div>
        </AnalyticsCard>
      </div>

      {/* Row 2: Handicap Distribution by Gender */}
      <AnalyticsCard title="Handicap Distribution by Gender">
        <div className="flex flex-col sm:flex-row gap-6">
          <div className="flex-1">
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={handicapByGender} margin={{ top: 20, right: 20, left: 0, bottom: 5 }} barCategoryGap="30%">
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                <XAxis
                  dataKey="name"
                  tick={{ fontSize: 11, fill: '#6b7280' }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#6b7280' }} axisLine={false} tickLine={false} />
                <Tooltip
                  formatter={(v: number) => [v, 'Players']}
                  contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: 12 }}
                />
                <Bar dataKey="count" name="Players" radius={[6, 6, 0, 0]}>
                  {handicapByGender.map((entry, i) => (
                    <Cell
                      key={i}
                      fill={
                        entry.name.startsWith('Male 0') ? '#2563eb' :
                        entry.name.startsWith('Male 19') ? '#60a5fa' :
                        entry.name.startsWith('Ladies 0') ? '#16a34a' :
                        '#4ade80'
                      }
                    />
                  ))}
                  <LabelList dataKey="count" position="top" style={{ fontSize: 13, fontWeight: 600, fill: '#374151' }} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="sm:w-52 flex flex-col justify-center gap-3">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Breakdown</p>
            {handicapSummary.map(item => (
              <div key={item.label} className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="w-3 h-3 rounded-sm flex-shrink-0" style={{ backgroundColor: item.color }} />
                  <span className="text-sm text-gray-600 truncate">{item.label}</span>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <span className="text-sm font-bold text-gray-800">{item.value}</span>
                  <span className="text-xs text-gray-400">
                    ({total > 0 ? Math.round((item.value / total) * 100) : 0}%)
                  </span>
                </div>
              </div>
            ))}
            <div className="mt-2 pt-3 border-t border-gray-100">
              <div className="flex justify-between text-xs text-gray-500">
                <span>Total tracked</span>
                <span className="font-semibold text-gray-700">{handicapSummary.reduce((s, i) => s + i.value, 0)}</span>
              </div>
            </div>
          </div>
        </div>
      </AnalyticsCard>

      {/* Row 3: Country + Club */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <AnalyticsCard title="Country Breakdown">
          <div className="space-y-1 max-h-72 overflow-y-auto pr-1">
            {countryBreakdown.map((item, i) => (
              <StatRow key={item.name} label={item.name} value={item.value} total={total} color={COLORS[i % COLORS.length]} />
            ))}
          </div>
        </AnalyticsCard>

        <AnalyticsCard title="Resident Club Breakdown">
          <div className="space-y-1 max-h-72 overflow-y-auto pr-1">
            {clubBreakdown.map((item, i) => (
              <StatRow key={item.name} label={item.name} value={item.value} total={total} color={COLORS[i % COLORS.length]} />
            ))}
          </div>
        </AnalyticsCard>
      </div>

      {/* Row 4: Shirt Size + Registration Status */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <AnalyticsCard title="Shirt Size Breakdown">
          <ResponsiveContainer width="100%" height={230}>
            <BarChart data={shirtSizeBreakdown} margin={{ top: 20, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="name" tick={{ fontSize: 12 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
              <Tooltip formatter={(v: number, name: string) => [v, name]} />
              <Legend />
              <Bar dataKey="Male" name="Male" fill="#2563eb" radius={[4, 4, 0, 0]}>
                <LabelList dataKey="Male" position="top" style={{ fontSize: 11, fill: '#2563eb', fontWeight: 600 }} formatter={(v: number) => v > 0 ? v : ''} />
              </Bar>
              <Bar dataKey="Female" name="Female" fill="#e11d48" radius={[4, 4, 0, 0]}>
                <LabelList dataKey="Female" position="top" style={{ fontSize: 11, fill: '#e11d48', fontWeight: 600 }} formatter={(v: number) => v > 0 ? v : ''} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </AnalyticsCard>

        <AnalyticsCard title="Registration Status">
          <div className="space-y-1 max-h-72 overflow-y-auto pr-1">
            {statusBreakdown.map((item, i) => (
              <StatRow key={item.name} label={item.name} value={item.value} total={total} color={COLORS[(i + 3) % COLORS.length]} />
            ))}
          </div>
          <div className="mt-4 pt-4 border-t border-gray-100 grid grid-cols-3 gap-2">
            {[
              { label: 'Info Complete', count: players.filter(p => p.registration_status?.personalInfo).length },
              { label: 'Payment Done', count: players.filter(p => p.registration_status?.payment).length },
              { label: 'Verified', count: players.filter(p => p.registration_status?.verification).length },
            ].map(step => (
              <div key={step.label} className="bg-gray-50 rounded-lg p-3 text-center">
                <p className="text-lg font-bold text-gray-800">{step.count}</p>
                <p className="text-xs text-gray-500 mt-0.5">{step.label}</p>
                <p className="text-xs text-gray-400">{total > 0 ? Math.round((step.count / total) * 100) : 0}%</p>
              </div>
            ))}
          </div>
        </AnalyticsCard>
      </div>
    </div>
  );
}
